import React from 'react';
import { Link } from 'react-router-dom';

export const Privacy = () => {
  return (
    <div className="privacy-container">
      {/* Retro grain texture overlay */}
      <div className="grain-overlay"></div>
      
      <div className="privacy-content">
        <Link to="/" className="privacy-back-link">← Back to Home</Link>
        
        <h1 className="privacy-title">Privacy Policy</h1>
        
        <div className="privacy-text">
          <p className="privacy-last-updated">Last updated: January 29, 2026</p>
          <div className="privacy-header-info">
            <p>Developer: Jayanth Putta ("Eranos," "we," "us")</p>
            <p>Contact: geteranos@gmail.com</p>
            <p>Privacy Policy URL: https://www.geteranos.com/privacy</p>
            <p>Support URL: https://www.geteranos.com/support</p>
          </div>
          
          <section className="privacy-section">
            <h2>Scope</h2>
            <p>
              This Privacy Policy applies to the Eranos iOS app and related services we operate to provide the app (including our backend). It does not attempt to comprehensively describe privacy practices for unrelated websites or products.
            </p>
          </section>

          <section className="privacy-section">
            <h2>Information we collect</h2>
            <h3 className="privacy-subsection">A. Account and identity information</h3>
            <p>
              When you create an account and sign in, we collect and store:
            </p>
            <ul>
              <li>Name</li>
              <li>Email address</li>
              <li>Authentication identifier:
                <ul>
                  <li>For Apple Sign In: Apple user identifier</li>
                  <li>For Google Sign In: we identify your account using your email address</li>
                </ul>
              </li>
              <li>Profile photo URL (Google Sign In only; we do not store the image itself)</li>
            </ul>
            <p>
              You can't edit your name or photo inside the app; these reflect your sign-in provider information.
            </p>
            <h3 className="privacy-subsection">B. Event and coordination information (user content)</h3>
            <p>
              Because Eranos is a potluck planning app, we collect and store information you and other participants add to events, including:
            </p>
            <ul>
              <li>Event name</li>
              <li>Event description</li>
              <li>Event date</li>
              <li>Event theme</li>
              <li>Event join code</li>
              <li>Event membership (who has joined an event)</li>
              <li>Potluck contributions such as:
                <ul>
                  <li>dish name</li>
                  <li>dish category</li>
                  <li>dish description/notes</li>
                  <li>the participant associated with the contribution (visible within the event)</li>
                </ul>
              </li>
            </ul>
            <p>
              Moderation and enforcement data: If an event organizer removes a participant or bans them from rejoining an event, we store an event-level record of that enforcement action (for example, an internal user identifier in an event's banned list) to enforce the organizer's decision and help keep events safe.
            </p>
            <h3 className="privacy-subsection">C. AI features and chat content</h3>
            <p>
              If you use AI recommendations or the chat feature, we collect and store:
            </p>
            <ul>
              <li>Your messages to the AI</li>
              <li>AI responses</li>
              <li>Relevant event/potluck details that are needed to generate recommendations (for example, the menu and event context)</li>
            </ul>
            <p>
              Although the chat UI may reset when you reopen an event, the messages are still stored on our servers unless your account is deleted.
            </p>
            <h3 className="privacy-subsection">D. Diagnostics information</h3>
            <p>
              We do not use third-party analytics or crash-reporting SDKs at this time.
            </p>
            <p>
              Apple and iOS may collect device analytics and crash logs at the operating system level depending on your device settings; those logs go to Apple, not to us.
            </p>
            <h3 className="privacy-subsection">E. Reports and safety submissions</h3>
            <p>
              When a user reports an event, dish entry, or user, we collect the report type, the relevant content/user identifier, and any optional reason text. We may also include relevant event and content details to investigate the report.
            </p>
          </section>

          <section className="privacy-section">
            <h2>How we use information</h2>
            <p>We use the information above to:</p>
            <ul>
              <li>Create and authenticate your account and keep you signed in</li>
              <li>Create, display, and manage events and potluck contributions</li>
              <li>Show real-time coordination within events (who is bringing what)</li>
              <li>Provide AI recommendations and AI chat responses</li>
              <li>Maintain service security and prevent abuse</li>
              <li>Safety and moderation: to respond to reports, remove content, and restrict or ban users from specific events to prevent abuse</li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2>How information is shared</h2>
            <h3 className="privacy-subsection">A. With other users in your events</h3>
            <p>
              Eranos is built for group coordination. Within an event, participants can see:
            </p>
            <ul>
              <li>your name (and profile photo if present via Google)</li>
              <li>your contribution(s) (dish name/category/description)</li>
              <li>the event membership list</li>
            </ul>
            <p>
              Event organizers may remove participants and their associated dish entries from an event; banned participants will be prevented from rejoining that event.
            </p>
            <h3 className="privacy-subsection">B. With service providers (processors)</h3>
            <p>
              We use service providers to operate Eranos. These providers process data on our behalf to deliver app functionality:
            </p>
            <ul>
              <li>Authentication providers: Apple and Google</li>
              <li>AI processing provider: OpenAI</li>
              <li>Hosting and infrastructure: MongoDB Atlas and Railway</li>
              <li>App build and updates: Expo</li>
              <li>Email delivery provider: Resend</li>
            </ul>
            <p>
              Report emails may include reporter identity (name/email/account ID) and the reported content details (dish/event/user details) so we can investigate.
            </p>
            <p>
              We do not sell your personal information and we do not share your personal information for third-party advertising.
            </p>
          </section>

          <section className="privacy-section">
            <h2>Third-Party Data Sharing with OpenAI</h2>
            <p>
              When you use Eranos AI features, we send your AI chat messages and relevant potluck/event context to OpenAI's GPT-4o-mini model to generate recommendations, recipes, and chat responses.
            </p>
            <p>
              We do not share personally identifiable information (such as your name or email address) with OpenAI. The data sent includes only your messages and event context (such as dish names, event themes, and menu details) needed to generate a useful response.
            </p>
            <p>
              OpenAI states that, by default, data sent to the OpenAI API is not used to train or improve OpenAI models unless the customer explicitly opts in. For more information, see <a href="https://openai.com/policies/privacy-policy" className="privacy-email-link" target="_blank" rel="noopener noreferrer">OpenAI's Privacy Policy</a>.
            </p>
          </section>

          <section className="privacy-section">
            <h2>Data retention</h2>
            <p>
              <strong>User accounts:</strong> retained until you delete your account.
            </p>
            <p>
              <strong>Sessions:</strong> session tokens expire after 7 days.
            </p>
            <p>
              <strong>AI conversation history:</strong> retained until you delete your account.
            </p>
            <p>
              <strong>AI recommendation cache:</strong> cached for 30 minutes.
            </p>
            <p>
              <strong>AI recipe cache:</strong> cached for 24 hours.
            </p>
            <p>
              <strong>Event and dish data:</strong> retained until the event is deleted or you leave the event.
            </p>
            <p>
              <strong>Infrastructure logs:</strong> retained by our hosting provider (Railway) for approximately 7 days.
            </p>
            <p>
              <strong>Backups:</strong> we do not maintain separate long-term backups at this time.
            </p>
            <p>
              <strong>Reports:</strong> retained as long as needed to investigate and resolve safety issues, and then deleted or de-identified unless we need to keep them longer for legal, security, or abuse-prevention purposes.
            </p>
            <p>
              <strong>Event enforcement records (bans/removals):</strong> retained as part of the event record for as long as the event exists (or until the event is deleted), solely to enforce organizer moderation decisions.
            </p>
          </section>

          <section className="privacy-section">
            <h2>Account deletion and what happens to your data</h2>
            <p>
              Eranos provides in-app account deletion (Profile → Delete Account). You can also delete your account on the web at <a href="https://www.geteranos.com/delete-account" className="privacy-email-link">geteranos.com/delete-account</a>. Apple requires apps that support account creation to let users initiate account deletion within the app. Apple Developer
            </p>
            <p>
              When you delete your account:
            </p>
            <ul>
              <li>We delete your account record and personal identifiers we are not legally required to keep (such as your email).</li>
              <li>We delete (or de-identify) AI chat content associated with your account.</li>
              <li>We preserve shared event history for other participants, but anonymize your identity in those historical records (for example, replacing your name with "Deleted user" for past contributions). Dish names/categories/descriptions remain for the integrity of the event history.</li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2>Security</h2>
            <p>
              We use reasonable administrative and technical safeguards designed to protect information (including encrypted connections between the app, our backend, and our database providers).
            </p>
          </section>

          <section className="privacy-section">
            <h2>International availability</h2>
            <p>
              Eranos is available worldwide. Your information may be processed in countries where we or our service providers operate.
            </p>
          </section>

          <section className="privacy-section">
            <h2>Children's privacy</h2>
            <p>
              Eranos is not directed to children under 13 and we do not knowingly collect personal information from children. If we become aware that we have inadvertently collected personal information from a child under 13, we will take steps to delete that information as soon as possible. If you believe a child under 13 has provided us with personal information, please contact us at <a href="mailto:geteranos@gmail.com" className="privacy-email-link">geteranos@gmail.com</a>.
            </p>
          </section>

          <section className="privacy-section">
            <h2>Data collected by third-party authentication providers</h2>
            <p>
              Eranos uses Google Sign-In and Apple Sign-In for authentication. When you sign in, Google or Apple may collect data (such as device identifiers, IP addresses, or usage data) according to their own privacy policies. We encourage you to review <a href="https://policies.google.com/privacy" className="privacy-email-link" target="_blank" rel="noopener noreferrer">Google's Privacy Policy</a> and <a href="https://www.apple.com/legal/privacy/" className="privacy-email-link" target="_blank" rel="noopener noreferrer">Apple's Privacy Policy</a> for details on the data they collect during authentication.
            </p>
          </section>

          <section className="privacy-section">
            <h2>Privacy Policy page analytics</h2>
            <p>
              The privacy policy webpage at https://www.geteranos.com/privacy uses Vercel Web Analytics, which is designed to operate without third-party cookies and uses a hash derived from the incoming request to identify visitor sessions (discarded after 24 hours). Vercel
            </p>
          </section>

          <section className="privacy-section">
            <h2>Contact us</h2>
            <p>
              Questions or requests: <a href="mailto:geteranos@gmail.com" className="privacy-email-link">geteranos@gmail.com</a>
            </p>
            <p>
              If you believe you were removed or banned in error, you can contact us at geteranos@gmail.com
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Privacy;

